import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Phone, Clock, CheckCircle, Calendar } from 'lucide-react';
import heroImage from '@/assets/hero-law.jpg';
import { useEffect } from 'react';
import { motion } from 'framer-motion';

const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: delay / 1000 }}
  >
    {children}
  </motion.div>
);

const PhoneConsultation = () => {
  useEffect(() => {
    // Cal.com embed script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = `
      (function (C, A, L) { 
        let p = function (a, ar) { a.q.push(ar); }; 
        let d = C.document; 
        C.Cal = C.Cal || function () { 
          let cal = C.Cal; 
          let ar = arguments; 
          if (!cal.loaded) { 
            cal.ns = {}; 
            cal.q = cal.q || []; 
            d.head.appendChild(d.createElement("script")).src = A; 
            cal.loaded = true; 
          } 
          if (ar[0] === L) { 
            const api = function () { p(api, arguments); }; 
            const namespace = ar[1]; 
            api.q = api.q || []; 
            if(typeof namespace === "string"){
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar); 
            return;
          } 
          p(cal, ar); 
        }; 
      })(window, "https://app.cal.com/embed/embed.js", "init");
      Cal("init", "90min", {origin:"https://app.cal.com"});
      Cal.ns["90min"]("ui", {"hideEventTypeDetails":false,"layout":"month_view"});
    `;
    document.head.appendChild(script);


    // Add custom styles for mobile Cal.com modal
    const style = document.createElement('style');
    style.innerHTML = `
      @media (max-width: 768px) {
        [id*="cal-"] > div,
        [class*="cal-modal"] {
          margin: 16px !important;
          max-width: calc(100vw - 32px) !important;
          width: calc(100vw - 32px) !important;
          left: 16px !important;
          right: 16px !important;
        }
        
        iframe[id*="cal-"] {
          max-width: 100% !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      // Cleanup script and style on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  return (
    <section id="phone-consultation" className="pt-32 pb-20 bg-background">
      <div className="container mx-auto px-4 max-w-6xl">
        <AnimatedSection delay={0}>
          <div className="mb-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
                <Phone className="h-7 w-7 text-gold-light" />
              </div>
              <h1 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-4">
                Consulting
              </h1>
            </div>
            <p className="text-lg text-muted-foreground leading-relaxed text-justify max-w-4xl mx-auto">
              This page is here for those who want to connect directly—whether you have questions about specific processes or need deeper consultation in other areas. Keep in mind: every process and service we offer is available completely free on our <Link to="/education" className="underline hover:text-primary transition-colors">Education</Link> page. So before reaching out, be sure to check there first. The knowledge is free. My time is not.
            </p>
          </div>
        </AnimatedSection>

        {/* Call to Action */}
        <AnimatedSection delay={200}>
          <div className="bg-gradient-to-br from-primary/10 to-gold/20 rounded-2xl p-8 md:p-12 text-center border border-gold/30 mt-8 max-w-3xl mx-auto">
            <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-6">
              Ready to Schedule Your Consultation?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Connect directly with our constitutional law experts for personalized guidance on your specific legal matters and questions.
            </p>
            
            <div className="flex justify-center">
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg"
                data-cal-link="freeman-and-crown/90min"
                data-cal-namespace="90min"
                data-cal-config='{"layout":"month_view"}'
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Now
              </Button>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default PhoneConsultation;